import { get as _get, set as _set, unset as _unset, isEmpty as _isEmpty } from 'lodash'
import { makeAutoObservable } from 'mobx'
import { type IAgentActionModel, type IAgentActionSettings } from 'entities/AgentActions'
import { ChatbotErrorsList, makeCleanCopy } from 'entities/Chatbot'
import { type AgentActionsStore } from 'pages/chatbot/pages/agentBookAppointment/pages/agentActions/store/agentActionsStore'
import { type IChatbotPayloadPath } from './type'
import { type ToErrorList } from '../utils'

type IErrorType = {
  success_actions: IAgentActionSettings[] | null
  fallback_actions: IAgentActionSettings[] | null
  qualified_actions: IAgentActionSettings[] | null
  disqualified_actions: IAgentActionSettings[] | null
}
export class AgentErrors {
  private _error: ToErrorList<IErrorType> = {}
  private _successActions = new ChatbotErrorsList<IAgentActionModel, IAgentActionSettings>(this)
  private _fallbackActions = new ChatbotErrorsList<IAgentActionModel, IAgentActionSettings>(this)
  private _qualifiedActions = new ChatbotErrorsList<IAgentActionModel, IAgentActionSettings>(this)
  private _disqualifiedActions = new ChatbotErrorsList<IAgentActionModel, IAgentActionSettings>(
    this
  )

  constructor(private _agentActions: AgentActionsStore) {
    makeAutoObservable(this)
  }

  sync(error?: ToErrorList<IErrorType> | null) {
    this.clear()

    if (!error) return

    Object.entries(error).forEach(([path, errors]) => _set(this._error, path, errors))

    this._successActions.sync((errors) =>
      this._error.success_actions?.forEach((_, index) =>
        errors.set(
          this._agentActions.successActionsModel.actions[index],
          `success_actions.${index}`
        )
      )
    )

    this._fallbackActions.sync((errors) =>
      this._error.fallback_actions?.forEach((_, index) =>
        errors.set(
          this._agentActions.fallbackActionsModel.actions[index],
          `fallback_actions.${index}`
        )
      )
    )

    this._qualifiedActions.sync((errors) =>
      this._error.qualified_actions?.forEach((_, index) =>
        errors.set(
          this._agentActions.chatbot.qualifiedActions.actions[index],
          `qualified_actions.${index}`
        )
      )
    )

    this._disqualifiedActions.sync((errors) =>
      this._error.disqualified_actions?.forEach((_, index) =>
        errors.set(
          this._agentActions.chatbot.disqualifiedActions.actions[index],
          `disqualified_actions.${index}`
        )
      )
    )
  }

  get successActions() {
    return this._successActions
  }

  get fallbackActions() {
    return this._fallbackActions
  }

  get qualifiedActions() {
    return this._qualifiedActions
  }

  get disqualifiedActions() {
    return this._disqualifiedActions
  }

  get error() {
    return this._error
  }

  clear() {
    this._error = {}
    this.successActions.clear()
    this.fallbackActions.clear()
    this.qualifiedActions.clear()
    this.disqualifiedActions.clear()
  }

  getError(path: IChatbotPayloadPath) {
    const error = _get(this._error, path, null)

    if (Array.isArray(error) && typeof error[0] === 'string') {
      return error[0]
    }

    return ''
  }

  hasError(path: IChatbotPayloadPath) {
    const error = _get(this._error, path, null)

    return !_isEmpty(error)
  }

  hasOwnError(path: IChatbotPayloadPath) {
    const error = _get(this._error, path, null)

    return (
      Array.isArray(error) &&
      !!error.length &&
      (error as unknown[]).every((item) => typeof item === 'string')
    )
  }

  removeError(path: IChatbotPayloadPath) {
    const deleted = _unset(this._error, path)

    if (deleted) this._error = makeCleanCopy(this._error)
  }
}
